import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, formatDistanceToNow } from 'date-fns';
import { searchAuditLogs, getAuditStats, exportAuditLogs, type AuditLog } from '../../api/adminClient';
import clsx from 'clsx';

// ============================================================================
// CONFIG
// ============================================================================

const actionColors: Record<string, string> = {
  create: 'bg-green-100 text-green-800',
  update: 'bg-blue-100 text-blue-800',
  delete: 'bg-red-100 text-red-800',
  login: 'bg-purple-100 text-purple-800',
  login_failed: 'bg-orange-100 text-orange-800',
  logout: 'bg-gray-100 text-gray-800',
};

const actionConfig: Record<string, { icon: string; color: string; label: string }> = {
  create: { icon: '+', color: 'bg-green-500', label: 'Created' },
  update: { icon: '~', color: 'bg-blue-500', label: 'Updated' },
  delete: { icon: '-', color: 'bg-red-500', label: 'Deleted' },
  import: { icon: '↓', color: 'bg-purple-500', label: 'Imported' },
  export: { icon: '↑', color: 'bg-orange-500', label: 'Exported' },
  login: { icon: '→', color: 'bg-indigo-500', label: 'Logged in' },
  logout: { icon: '←', color: 'bg-gray-500', label: 'Logged out' },
  assign: { icon: '⊕', color: 'bg-teal-500', label: 'Assigned' },
  revoke: { icon: '⊖', color: 'bg-amber-500', label: 'Revoked' },
};

const entityIcons: Record<string, string> = {
  admin_user: '👤', user: '👥', employee: '👥', customer: '🏢',
  product: '📦', team: '🏠', role: '🔐', ownership: '🔗',
  report: '📊', settings: '⚙️', manager_relationship: '🔄',
  team_membership: '🏠',
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

type ViewMode = 'timeline' | 'table';

export default function AdminAuditTrail() {
  const [viewMode, setViewMode] = useState<ViewMode>('timeline');
  const [filters, setFilters] = useState({
    entity_types: [] as string[],
    actions: [] as string[],
    start_date: '',
    end_date: '',
  });
  const [page, setPage] = useState(0);
  const pageSize = 50;
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const { data: results, isLoading } = useQuery({
    queryKey: ['audit-trail', filters, page, viewMode],
    queryFn: () =>
      searchAuditLogs({
        entity_types: filters.entity_types.length > 0 ? filters.entity_types : undefined,
        actions: filters.actions.length > 0 ? filters.actions : undefined,
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
        limit: viewMode === 'timeline' ? 100 : pageSize,
        offset: viewMode === 'timeline' ? 0 : page * pageSize,
      }),
    refetchInterval: viewMode === 'timeline' ? 30000 : undefined,
  });

  const { data: stats } = useQuery({
    queryKey: ['audit-stats'],
    queryFn: () => getAuditStats(),
  });

  const entityTypes = stats?.by_entity_type ? Object.keys(stats.by_entity_type) : [];
  const actions = stats?.by_action ? Object.keys(stats.by_action) : [];

  const handleExport = async (fmt: 'csv' | 'json') => {
    setIsExporting(true);
    try {
      await exportAuditLogs(fmt, {
        entity_types: filters.entity_types.length > 0 ? filters.entity_types : undefined,
        actions: filters.actions.length > 0 ? filters.actions : undefined,
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
      });
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  const toggleFilter = (type: 'entity_types' | 'actions', value: string) => {
    setFilters(prev => {
      const arr = prev[type];
      const next = arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value];
      return { ...prev, [type]: next };
    });
    setPage(0);
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Trail</h1>
          <p className="text-gray-500 mt-1">Track all changes made across the system</p>
        </div>
        <div className="flex items-center gap-3">
          {/* View toggle */}
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            <button
              onClick={() => setViewMode('timeline')}
              className={clsx('px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                viewMode === 'timeline' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              )}
            >
              Timeline
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={clsx('px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                viewMode === 'table' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
              )}
            >
              Table
            </button>
          </div>
          {/* Export */}
          <button onClick={() => handleExport('csv')} disabled={isExporting}
            className="px-3 py-1.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-lg disabled:opacity-50">
            Export CSV
          </button>
          <button onClick={() => handleExport('json')} disabled={isExporting}
            className="px-3 py-1.5 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-lg disabled:opacity-50">
            Export JSON
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="text-2xl font-bold text-gray-900">{stats.total_logs.toLocaleString()}</div>
            <div className="text-sm text-gray-500">Total Events</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="text-2xl font-bold text-gray-900">{Object.keys(stats.by_entity_type).length}</div>
            <div className="text-sm text-gray-500">Entity Types</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="text-2xl font-bold text-gray-900">{Object.keys(stats.by_action).length}</div>
            <div className="text-sm text-gray-500">Action Types</div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
            <div className="text-2xl font-bold text-gray-900">{stats.by_day.length}</div>
            <div className="text-sm text-gray-500">Days with Activity</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-sm text-gray-500 mr-1">Actions:</span>
          {actions.map((action) => {
            const cfg = actionConfig[action] || { label: action };
            return (
              <button key={action} onClick={() => toggleFilter('actions', action)}
                className={clsx('px-2.5 py-1 text-xs rounded-full border transition-colors',
                  filters.actions.includes(action)
                    ? 'bg-primary-100 border-primary-300 text-primary-700'
                    : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                )}>
                {cfg.label}
              </button>
            );
          })}
        </div>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-sm text-gray-500 mr-1">Entities:</span>
          {entityTypes.map((entity) => (
            <button key={entity} onClick={() => toggleFilter('entity_types', entity)}
              className={clsx('px-2.5 py-1 text-xs rounded-full border transition-colors',
                filters.entity_types.includes(entity)
                  ? 'bg-primary-100 border-primary-300 text-primary-700'
                  : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
              )}>
              {entityIcons[entity] || '📄'} {entity}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">Start Date</label>
            <input type="date" value={filters.start_date}
              onChange={(e) => { setFilters({ ...filters, start_date: e.target.value }); setPage(0); }}
              className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-primary-500 outline-none" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-0.5">End Date</label>
            <input type="date" value={filters.end_date}
              onChange={(e) => { setFilters({ ...filters, end_date: e.target.value }); setPage(0); }}
              className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-1 focus:ring-primary-500 outline-none" />
          </div>
          {(filters.entity_types.length > 0 || filters.actions.length > 0 || filters.start_date || filters.end_date) && (
            <button onClick={() => { setFilters({ entity_types: [], actions: [], start_date: '', end_date: '' }); setPage(0); }}
              className="text-xs text-gray-500 hover:text-gray-700 mt-4">
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      {viewMode === 'timeline' ? (
        <TimelineView logs={results?.logs || []} isLoading={isLoading} />
      ) : (
        <TableView
          logs={results?.logs || []}
          total={results?.total || 0}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onSelectLog={setSelectedLog}
          isLoading={isLoading}
        />
      )}

      {/* Detail Modal */}
      {selectedLog && (
        <AuditLogDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />
      )}
    </div>
  );
}

// ============================================================================
// TIMELINE VIEW
// ============================================================================

function TimelineView({ logs, isLoading }: { logs: AuditLog[]; isLoading: boolean }) {
  // Group by date
  const grouped: Record<string, AuditLog[]> = {};
  for (const log of logs) {
    const date = format(new Date(log.created_at), 'yyyy-MM-dd');
    if (!grouped[date]) grouped[date] = [];
    grouped[date].push(log);
  }

  if (isLoading) return <div className="bg-white rounded-xl shadow-sm border p-8 text-center text-gray-500">Loading...</div>;
  if (logs.length === 0) return <div className="bg-white rounded-xl shadow-sm border p-8 text-center text-gray-500">No activity found</div>;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="divide-y divide-gray-100">
        {Object.entries(grouped).map(([date, items]) => (
          <div key={date}>
            <div className="px-6 py-3 bg-gray-50 sticky top-0">
              <h3 className="text-sm font-medium text-gray-700">
                {format(new Date(date), 'EEEE, MMMM d, yyyy')}
              </h3>
            </div>
            <div className="divide-y divide-gray-50">
              {items.map((log) => {
                const cfg = actionConfig[log.action] || { icon: '?', color: 'bg-gray-500', label: log.action };
                const entityIcon = entityIcons[log.entity_type] || '📄';
                return (
                  <div key={log.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start gap-4">
                      <div className={clsx('w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0', cfg.color)}>
                        {cfg.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-medium text-gray-900">{log.actor_email || 'System'}</span>
                          <span className="text-sm text-gray-500">{cfg.label}</span>
                          <span className="text-lg">{entityIcon}</span>
                          {log.entity_name && <span className="text-sm font-medium text-gray-700">{log.entity_name}</span>}
                        </div>
                        {log.description && <p className="text-sm text-gray-500">{log.description}</p>}
                      </div>
                      <div className="text-sm text-gray-400 flex-shrink-0">
                        {formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// TABLE VIEW
// ============================================================================

function TableView({
  logs, total, page, pageSize, onPageChange, onSelectLog, isLoading,
}: {
  logs: AuditLog[]; total: number; page: number; pageSize: number;
  onPageChange: (p: number) => void; onSelectLog: (l: AuditLog) => void; isLoading: boolean;
}) {
  if (isLoading) return <div className="bg-white rounded-xl shadow-sm border p-8 text-center text-gray-500">Loading...</div>;
  if (logs.length === 0) return <div className="bg-white rounded-xl shadow-sm border p-8 text-center text-gray-500">No audit logs found</div>;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <table className="w-full">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Timestamp</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actor</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Entity</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {logs.map((log) => (
            <tr key={log.id} onClick={() => onSelectLog(log)} className="hover:bg-gray-50 cursor-pointer">
              <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                {new Date(log.created_at).toLocaleString()}
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-900">{log.actor_email || 'System'}</div>
                {log.actor_ip && <div className="text-xs text-gray-500">{log.actor_ip}</div>}
              </td>
              <td className="px-6 py-4">
                <span className={clsx('px-2 py-1 text-xs font-medium rounded-full', actionColors[log.action] || 'bg-gray-100 text-gray-800')}>
                  {log.action}
                </span>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-900">{log.entity_type}</div>
                {log.entity_name && <div className="text-xs text-gray-500">{log.entity_name}</div>}
              </td>
              <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                {log.description || '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination */}
      <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
        <div className="text-sm text-gray-500">
          Showing {page * pageSize + 1} to {Math.min((page + 1) * pageSize, total)} of {total.toLocaleString()} results
        </div>
        <div className="flex gap-2">
          <button onClick={() => onPageChange(Math.max(0, page - 1))} disabled={page === 0}
            className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 text-sm">Previous</button>
          <button onClick={() => onPageChange(page + 1)} disabled={(page + 1) * pageSize >= total}
            className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 text-sm">Next</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// DETAIL MODAL
// ============================================================================

function AuditLogDetailModal({ log, onClose }: { log: AuditLog; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Audit Log Detail</h2>
          <p className="text-sm text-gray-500 mt-1">{log.id}</p>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Timestamp</label>
              <p className="text-gray-900">{new Date(log.created_at).toLocaleString()}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Action</label>
              <span className={clsx('px-2 py-1 text-xs font-medium rounded-full', actionColors[log.action] || 'bg-gray-100 text-gray-800')}>
                {log.action}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Actor</label>
              <p className="text-gray-900">{log.actor_email || 'System'}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">IP Address</label>
              <p className="text-gray-900">{log.actor_ip || 'N/A'}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Entity Type</label>
              <p className="text-gray-900">{log.entity_type}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Entity ID</label>
              <p className="text-gray-900 font-mono text-sm">{log.entity_id || 'N/A'}</p>
            </div>
          </div>

          {log.entity_name && (
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Entity Name</label>
              <p className="text-gray-900">{log.entity_name}</p>
            </div>
          )}

          {log.description && (
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Description</label>
              <p className="text-gray-900">{log.description}</p>
            </div>
          )}

          {log.old_values && (
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Previous Values</label>
              <pre className="bg-gray-50 p-3 rounded-lg text-sm overflow-x-auto">
                {JSON.stringify(log.old_values, null, 2)}
              </pre>
            </div>
          )}

          {log.new_values && (
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">New Values</label>
              <pre className="bg-gray-50 p-3 rounded-lg text-sm overflow-x-auto">
                {JSON.stringify(log.new_values, null, 2)}
              </pre>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
